import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

const NAMED_ROLES = [
  ["primary_owner_id", "Primary Owner", "High"],
  ["owner_user_id", "Account Owner", "High"],
  ["technical_expert_id", "Technical Expert", "Medium"],
  ["sme_id", "SME", "Medium"],
  ["founder_sponsor_id", "Founder Sponsor", "High"],
  ["delivery_lead_id", "Delivery Lead", "Medium"],
];

const PRIORITY_STYLE = {
  High: "bg-[#FDE8D2] text-[#B45309] border-[#F6C889]",
  Medium: "bg-[#E7F5FB] text-[#07539A] border-[#BBD8E7]",
  Standard: "bg-[#F2F4F5] text-[#607D8B] border-[#D7E0E4]",
  Low: "bg-white text-[#78909C] border-[#D7E0E4]",
  Primary: "bg-[#E3F3E7] text-[#15803D] border-[#A9D8B4]",
  Supporting: "bg-[#E7F5FB] text-[#07539A] border-[#BBD8E7]",
};

/** Flat ownership summary used on opportunity detail. Editing stays in Manage Team. */
export function TeamOwnershipTable({ entityType, entityId, refreshKey = 0 }) {
  const [share, setShare] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      api.get(`/share/${entityType}/${entityId}`),
      api.get(`/partner-assignments/${entityType}/${entityId}`).catch(() => ({ data: [] })),
    ]).then(([s, p]) => {
      if (!active) return;
      setShare(s.data);
      setPartners(Array.isArray(p.data) ? p.data : []);
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [entityType, entityId, refreshKey]);

  const rows = useMemo(() => {
    if (!share) return [];
    const users = share.users || [];
    const umap = Object.fromEntries(users.map((u) => [u.id, u]));
    const seen = new Set();
    const result = [];
    const addUser = (uid, role, priority) => {
      if (!uid || seen.has(`${uid}:${role}`)) return;
      const user = umap[uid] || {};
      result.push({
        key: `user-${uid}-${role}`,
        member: user.name || user.email || uid.slice(0, 8),
        role,
        priority,
        commission: "—",
      });
      seen.add(`${uid}:${role}`);
    };

    NAMED_ROLES.forEach(([field, role, priority]) => addUser(share[field], role, priority));
    (share.collaborators || []).forEach((uid) => addUser(uid, "Collaborator", "Standard"));
    (share.watchers || []).forEach((uid) => addUser(uid, "Watcher", "Low"));
    partners.filter((p) => p.status === "active").forEach((p) => {
      result.push({
        key: `partner-${p.id}`,
        member: p.partner_name || "Partner",
        role: p.is_primary ? "Primary Partner" : "Partner",
        priority: p.is_primary ? "Primary" : "Supporting",
        commission: p.commission_pct == null ? "—" : `${p.commission_pct}%`,
      });
    });
    return result;
  }, [share, partners]);

  if (loading) {
    return <div className="min-h-[5rem] flex items-center justify-center text-[11px] text-[#607D8B]"><Loader2 size={13} className="animate-spin mr-2" /> Loading team…</div>;
  }

  return (
    <div className="overflow-x-auto" data-testid="team-ownership-table">
      <div className="min-w-[520px]">
        <div className="grid grid-cols-[minmax(180px,1.5fr)_minmax(130px,1fr)_100px_100px] gap-3 px-3 py-2 border-b border-[#D7E8F0] text-[10px] uppercase tracking-widest font-semibold text-[#07539A]">
          <div>Member</div><div>Role</div><div>Priority</div><div className="text-right">Commission</div>
        </div>
        {rows.length === 0 ? (
          <div className="px-3 py-5 text-center text-[12px] text-[#78909C]">No team members assigned. Use Manage Team to add people or partners.</div>
        ) : (
          <div className="divide-y divide-[#E1EBF0]">
            {rows.map((row) => (
              <div key={row.key} className="grid grid-cols-[minmax(180px,1.5fr)_minmax(130px,1fr)_100px_100px] gap-3 items-center px-3 py-2.5 text-[12px]">
                <div className="font-medium text-[#12324A] truncate">{row.member}</div>
                <div className="text-[#526D7A] truncate">{row.role}</div>
                <div><span className={`inline-flex px-2 py-0.5 rounded-full border text-[9px] uppercase tracking-wider ${PRIORITY_STYLE[row.priority]}`}>{row.priority}</span></div>
                <div className="text-right font-mono tabular-nums font-semibold text-[#07539A]">{row.commission}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
